import type {
  AnalyticsQuery,
  MetricName,
} from "schemas/generated/analyticsRead";
import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import {
  DEFAULT_QUERY,
  METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA,
} from "@v2/features/analytics/constants";
import useAnalyticsStatus from "@v2/features/analytics/hooks/useAnalyticsStatus";
import useCurrentAnalyticsCurrencyCode from "@v2/features/analytics/hooks/useCurrentAnalyticsCurrencyCode";
import useUrlHosts from "@v2/features/analytics/hooks/useUrlHosts";
import { generateDeepDiveNavigationPathFromUrl } from "@v2/features/analytics/utils/deepDiveNavigation";
import { formatValueToTableCell } from "@v2/features/analytics/utils/formatMetric";
import useDirectAnalyticsRead from "@v2/features/home/hooks/useDirectAnalyticsRead";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { getRangesFromTimePeriod } from "analytics-utils/analytics";
import { LineChart, Lock, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const usePageAnalytics = ({
  url,
  path,
}: {
  url: string | null;
  path: string;
}) => {
  const { workspaceId, workspace } = useCurrentWorkspaceContext();
  const { project } = useCurrentProjectContext();
  const projectId = project?.id;

  const { isLoading: isLoadingAnalyticsStatus, shouldBlurFullScreen } =
    useAnalyticsStatus();

  const urlHostsObj = useUrlHosts();

  const { urlSyncedQuery, deepDivePathWithCompressedUrlSyncedQuery } =
    generateDeepDiveNavigationPathFromUrl({
      url,
      urlHosts: urlHostsObj.data ?? [],
      path,
      workspaceId: workspaceId ?? "",
      projectId: projectId ?? null,
      selectedTimePeriod: {
        type: "relative",
        value: "last-7-days",
      },
    });

  const { updatedRanges: ranges } = getRangesFromTimePeriod({
    selectedTimePeriod: urlSyncedQuery.selectedTimePeriod,
    timeZone: project?.timeZone ?? workspace?.timeZone ?? "UTC",
  });

  const query: AnalyticsQuery = {
    ...DEFAULT_QUERY,
    urlHosts: urlSyncedQuery.urlHosts,
    ranges,
    filters: urlSyncedQuery.filters,
    mode: "groupByUrlPath",
    isFractional: false,
  };

  const { mainRangeResults, isLoading: isLoadingAnalytics } =
    useDirectAnalyticsRead({ query });

  const metrics = mainRangeResults[0]?.metrics;
  const sessions = metrics?.unique_sessions?.[0] ?? null;
  const earnings = metrics?.revenue?.[0] ?? null;
  const conversionRate = metrics?.conversion_rates?.[0] ?? null;

  return {
    sessions,
    earnings,
    conversionRate,
    isLoading: isLoadingAnalytics || isLoadingAnalyticsStatus,
    deepDivePathWithCompressedUrlSyncedQuery,
    shouldBlurFullScreen,
  };
};

const PageCardMetricValue: React.FC<{
  name: MetricName;
  value: number | null;
  valueClassName?: string;
}> = ({ name, value, valueClassName }) => {
  const currency = useCurrentAnalyticsCurrencyCode();

  return (
    <div className={twMerge("min-w-[10px]", valueClassName)}>
      {value !== null ||
      METRICS_ALLOWING_ZERO_VALUE_WHEN_NO_DATA.includes(name) ? (
        formatValueToTableCell({
          value: value ?? 0,
          name,
          currency,
          isFractional: false,
        })
      ) : (
        <Minus size={14} />
      )}
    </div>
  );
};

export default function PageCardAnalytics({
  file,
  publishedSiteUrl,
}: {
  file: FileWithLatestFileVersion;
  publishedSiteUrl?: string;
}) {
  const projectId = useCurrentProjectId();
  const {
    sessions,
    // earnings,
    // conversionRate,
    deepDivePathWithCompressedUrlSyncedQuery,
    shouldBlurFullScreen,
  } = usePageAnalytics({
    url: publishedSiteUrl ?? null,
    path: file.pageRoute ?? "",
  });
  const navigate = useNavigate();

  const metricsToDisplay = [
    {
      label: "Sessions",
      value: sessions,
      metricName: "unique_sessions" as const,
    },
    // NOTE (Ben, 2025-09-15): We're hiding earnings and conversion rate for now since they don't work on V2 yet
    // {
    //   label: "Earnings",
    //   value: earnings,
    //   metricName: "revenue" as const,
    // },
    // {
    //   label: "Conversion",
    //   value: conversionRate,
    //   metricName: "conversion_rates" as const,
    // },
  ];

  return (
    <div className="relative w-full">
      <div className="grid grid-cols-3 w-full">
        {metricsToDisplay.map((metric) => (
          <div key={metric.label} className="space-y-0.5">
            <p className="text-xs text-placeholder">{metric.label}</p>
            <p className="header-h6">
              <PageCardMetricValue
                name={metric.metricName}
                value={metric.value}
                valueClassName={shouldBlurFullScreen ? "blur-xs" : undefined}
              />
            </p>
          </div>
        ))}
      </div>
      <div className="absolute bottom-0 right-0">
        <Tooltip
          content={
            shouldBlurFullScreen ? "Upgrade for analytics" : "Last 7 days"
          }
        >
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              if (shouldBlurFullScreen) {
                if (!projectId) {
                  return;
                }

                navigate(`/shops/${projectId}/analytics/insights`);
              } else {
                if (!deepDivePathWithCompressedUrlSyncedQuery) {
                  return;
                }

                navigate(deepDivePathWithCompressedUrlSyncedQuery);
              }
            }}
          >
            {shouldBlurFullScreen ? <Lock /> : <LineChart />}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}
